import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { readVarT } from '../../Assertions/environments';
import { currentUserStore } from '../../CurrentUserStore';
import { UserResource } from '../../CurrentUserStore/Types';
import { useFetch, useFindLinkByR } from '../../Fetch';
import { organizationResourceDecoder } from '../../Organization/Decoder';
import { OrganizationResource } from '../../Organization/Types';
import { sessionStore } from '../../Session/Store';
import { CompanyRepresentation, IntercomInitData } from './Types';

function IntercomTooling() {
  const state = currentUserStore.currentUserState;
  switch (state.kind) {
    case 'identified':
      return <_IntercomTooling currentUserResource={state.currentUserResource} />;
    case 'anonymous':
    case 'unauthorized':
    case 'waiting':
    case 'identifying':
    case 'errored':
    case 'logging-out':
      return <></>;
  }
}

interface Props {
  currentUserResource: UserResource;
}

function impersonatingUserId(): string {
  const state = sessionStore.state;
  switch (state.kind) {
    case 'present':
      return state.sessionData.impersonatingUserId.map(String).getOrElseValue('');
    case 'absent':
    case 'errored':
    case 'requesting':
      return '';
  }
}

function companyMappingData(organization: OrganizationResource): CompanyRepresentation {
  const {
    id,
    name,
    plans,
    progressEmailsCommunicationPreference,
    registrationInvitationCommunicationPreference,
    chatPreference,
    createdAt,
    university,
    activeIntegrations,
  } = organization.payload;
  return {
    id: fromNumber(id),
    name: name,
    plan: fromArray(plans),
    'Progress Emails Communication Preference': progressEmailsCommunicationPreference,
    'Registration Invitation Emails Communication Preference':
      registrationInvitationCommunicationPreference,
    'Chat Preference': chatPreference,
    'Active Integrations': fromArray(activeIntegrations),
    University: university,
    created_at: asUnixTimestamp(createdAt),
  };
}

function asUnixTimestamp(date: Date): number {
  return date.getTime() / 1000;
}

function intercomString(value: string): string {
  const MAX_LENGTH = 252; // 255 - 3 for ellipsis
  return value.length > MAX_LENGTH ? `${value.slice(0, MAX_LENGTH)}...` : value;
}

function maybeIntercomString(value: Maybe<string>): string {
  return value.map(intercomString).getOrElseValue('');
}

function fromNumber(value: number): string {
  return intercomString(value.toString());
}

function fromArray(value: string[]): string {
  return intercomString(value.join(','));
}

function resetIntercomHack() {
  window.Intercom = undefined;
}

function _IntercomTooling({ currentUserResource }: Props) {
  const user = currentUserResource.payload;
  const organizationLink = useFindLinkByR({ rel: 'organization' }, currentUserStore.links);
  const organizationState = useFetch(organizationLink, organizationResourceDecoder);

  const location = useLocation();

  useEffect(() => {
    window.Intercom?.('update', {
      last_page: location.pathname,
    });
  }, [location]);

  useEffect(() => {
    if (organizationState.kind !== 'ready') {
      return;
    }

    readVarT('VITE_INTERCOM_APP_ID')
      .map<IntercomInitData>((appId) => ({
        app_id: appId,
        name: intercomString(user.shortName),
        email: intercomString(user.email),
        user_id: user.platformUserId.map(fromNumber).map(intercomString).getOrElseValue(''),
        created_at: asUnixTimestamp(user.createdAt),
        'Salesforce Contact ID': maybeIntercomString(user.contactUID),
        'Organization ID': intercomString(fromNumber(user.organizationId)),
        'Organization Name': intercomString(user.organizationName),
        company: companyMappingData(organizationState.data),
        'Salesforce Account ID': intercomString(user.organizationAccountUID),
        'Salesforce Platform Org ID': intercomString(user.organizationPlatformOrgUID),
        'Product License IDs': user.productLicenseIds.join(','),
        'Product License Type IDs': fromArray(user.productLicenseTypeIds.map(String)),
        'Admin Privilege IDs': fromArray(user.epPrivilegeIds.map(String)),
        'Impersonator ID': intercomString(impersonatingUserId()),
        'Login Type': intercomString(user.loginType),
        'Enrollment Details': fromArray(user.enrollmentDetails),
        'User Product License Details': fromArray(user.userProductLicenseDetails),
      }))
      .fork(
        () => {
          console.warn(
            'Unable to load Intercom: Missing environment variable VITE_INTERCOM_APP_ID',
          );
        },
        (data) => {
          resetIntercomHack();
          Intercom(data);
        },
      );

    return shutdown;
  }, [user, organizationState]);

  return <></>;
}

export default observer(IntercomTooling);
